import { useState, useEffect } from 'react';
import { usePromotersData, useProspectsData, useMeetingsData } from './useApi';
import { Meeting, Promoter, Prospect } from '@clareefai/types';

export const useData = () => {
  // Use API hooks to get data
  const { promoters, loading: promotersLoading, refetch: refetchPromoters } = usePromotersData();
  const { prospects, loading: prospectsLoading, refetch: refetchProspects } = useProspectsData();
  const { meetings, loading: meetingsLoading, refetch: refetchMeetings } = useMeetingsData();

  // Local state for optimistic updates
  const [localMeetings, setLocalMeetings] = useState<Meeting[]>([]);
  const [localPromoters, setLocalPromoters] = useState<Promoter[]>([]);
  const [localProspects, setLocalProspects] = useState<Prospect[]>([]);

  // Update local state when API data changes
  useEffect(() => {
    if (meetings) setLocalMeetings(meetings);
  }, [meetings]);

  useEffect(() => {
    if (promoters) setLocalPromoters(promoters);
  }, [promoters]);

  useEffect(() => {
    if (prospects) setLocalProspects(prospects);
  }, [prospects]);

  const updateMeetingStatus = (id: string, newStatus: Meeting['status']) => {
    setLocalMeetings(prev => prev.map(meeting => 
      meeting.id === id ? { ...meeting, status: newStatus } : meeting
    ));
    // TODO: Call API to update on server
  };

  const updatePromoterStatus = (id: string, newStatus: Promoter['status']) => {
    setLocalPromoters(prev => prev.map(promoter => 
      promoter.id === id ? { ...promoter, status: newStatus } : promoter
    ));
    // TODO: Call API to update on server
  };

  const updateProspectStatus = (id: string, newStatus: Prospect['status']) => {
    setLocalProspects(prev => prev.map(prospect => 
      prospect.id === id ? { ...prospect, status: newStatus } : prospect
    ));
    // TODO: Call API to update on server
  };

  const addPromoter = (promoter: Omit<Promoter, 'id'>) => {
    const newPromoter: Promoter = {
      ...promoter,
      id: Date.now().toString(),
    };
    setLocalPromoters(prev => [...prev, newPromoter]);
    // TODO: Call API to create on server
  };

  const addProspect = (prospect: Omit<Prospect, 'id'>) => {
    const newProspect: Prospect = {
      ...prospect,
      id: Date.now().toString(),
    };
    setLocalProspects(prev => [...prev, newProspect]);
    // TODO: Call API to create on server
  };

  const addMeeting = (meeting: Omit<Meeting, 'id'>) => {
    const newMeeting: Meeting = {
      ...meeting,
      id: Date.now().toString(),
    };
    setLocalMeetings(prev => [...prev, newMeeting]);
    // TODO: Call API to create on server
  };

  const deleteProspect = (id: string) => {
    setLocalProspects(prev => prev.filter(prospect => prospect.id !== id));
    // TODO: Call API to delete on server
  };

  const updateProspectPromoters = (prospectId: string, newPromoterNames: string) => {
    setLocalProspects(prev => prev.map(prospect => 
      prospect.id === prospectId 
        ? { ...prospect, promoterName: newPromoterNames }
        : prospect
    ));
    // TODO: Call API to update on server
  };

  const importFromHubSpot = () => {
    // TODO: Call API to import from HubSpot
    console.log('Importing from HubSpot...');
    refetchPromoters();
  };

  return {
    meetings: localMeetings,
    promoters: localPromoters,
    prospects: localProspects,
    loading: promotersLoading || prospectsLoading || meetingsLoading,
    updateMeetingStatus,
    updatePromoterStatus,
    updateProspectStatus,
    addPromoter,
    addProspect,
    addMeeting,
    deleteProspect,
    updateProspectPromoters,
    importFromHubSpot,
    refetch: () => {
      refetchPromoters();
      refetchProspects();
      refetchMeetings();
    }
  };
};